import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Space, Row, Col, Statistic,
    message, Typography, Avatar, Empty, Progress, Spin, Collapse, Tabs, Modal, Input, Select, Dropdown, Segmented, Divider
} from 'antd';
import {
    ArrowLeftOutlined, ClockCircleOutlined, CheckCircleOutlined,
    TeamOutlined, CrownOutlined, CarOutlined, PhoneOutlined,
    CalendarOutlined, WalletOutlined, TrophyOutlined, DollarOutlined,
    DownloadOutlined, FileExcelOutlined, ReloadOutlined, SendOutlined, EditOutlined,
    SyncOutlined, UserOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import XLSX from 'xlsx-js-style';

dayjs.locale('fr');

const { Title, Text } = Typography;
const { Panel } = Collapse;

const Commissions130hDetailPage = () => {
    const { type, beneficiaryId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [hoursThreshold, setHoursThreshold] = useState(130);
    const [commissionRates, setCommissionRates] = useState({ affiliate: 900, ambassador: 100, direct: 1000 });
    const selectedMonth = searchParams.get('month') || dayjs().format('YYYY-MM');

    // Payment modal states
    const [paymentModal, setPaymentModal] = useState({ visible: false, driver: null });
    const [paymentRef, setPaymentRef] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('paid');
    const [savingPayment, setSavingPayment] = useState(false);
    const [paymentMode, setPaymentMode] = useState('wave');
    const [sendingWave, setSendingWave] = useState(false);

    // Details modal state
    const [detailsModal, setDetailsModal] = useState({ visible: false, driver: null });

    // Export state
    const [exporting, setExporting] = useState(false);

    // Snapshot state
    const [snapshotting, setSnapshotting] = useState(false);

    // Déterminer si on vient de la page revenus ou commissions
    const isFromRevenues = location.pathname.includes('/revenues-130h');

    const fetchDetail = useCallback(async () => {
        setLoading(true);
        try {
            const response = await affiliatesService.get130hBeneficiaryDetail(type, beneficiaryId, {
                month: selectedMonth
            });

            if (response.success) {
                setData(response.data);
                if (response.data.hoursThreshold) {
                    setHoursThreshold(response.data.hoursThreshold);
                }
                if (response.data.commissionRates) {
                    setCommissionRates(prev => ({ ...prev, ...response.data.commissionRates }));
                }
            } else {
                message.error('Bénéficiaire non trouvé');
                navigate(isFromRevenues ? '/dashboard/revenues-130h' : '/dashboard/commissions-130h');
            }
        } catch (error) {
            message.error('Erreur lors du chargement');
            navigate(isFromRevenues ? '/dashboard/revenues-130h' : '/dashboard/commissions-130h');
        } finally {
            setLoading(false);
        }
    }, [type, beneficiaryId, selectedMonth, navigate, isFromRevenues]);

    // Snapshot (Recalculer) + rechargement des données
    const handleSnapshot = useCallback(async () => {
        setSnapshotting(true);
        try {
            await affiliatesService.snapshot130hHours(selectedMonth);
            message.success('Heures mises à jour avec succès');
            await fetchDetail();
        } catch (error) {
            console.error('Erreur snapshot 130h:', error);
            message.error('Erreur lors de la mise à jour des heures');
        } finally {
            setSnapshotting(false);
        }
    }, [selectedMonth, fetchDetail]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    // Handler pour afficher les détails
    const handleDetailsClick = (driver) => {
        setDetailsModal({ visible: true, driver });
    };

    // Handlers pour le paiement
    const handlePaymentClick = (driver) => {
        setPaymentModal({ visible: true, driver });
        setPaymentRef(driver.paymentReference || '');
        setPaymentStatus(driver.commissionStatus === 'paid' ? 'paid' : 'paid');
        setPaymentMode('wave');
    };

    const handleClosePaymentModal = () => {
        setPaymentModal({ visible: false, driver: null });
        setPaymentRef('');
        setPaymentMode('wave');
    };

    // Paiement via Wave
    const handleWavePayout = async () => {
        if (!paymentModal.driver) return;

        const driver = paymentModal.driver;
        const amount = driver.potentialAmount || commissionRates.affiliate;

        setSendingWave(true);
        try {
            const response = await affiliatesService.send130hWavePayout({
                driverId: driver._id,
                beneficiaryId: beneficiaryId,
                beneficiaryType: type,
                month: selectedMonth,
                amount: amount
            });

            if (response.success) {
                message.success(`Paiement Wave de ${amount.toLocaleString()} F envoyé avec succès`);
                handleClosePaymentModal();
                fetchDetail();
            } else {
                message.error(response.message || 'Erreur lors du paiement Wave');
            }
        } catch (error) {
            console.error('Erreur paiement Wave 130h:', error);
            message.error('Erreur lors du paiement Wave');
        } finally {
            setSendingWave(false);
        }
    };

    // Paiement manuel
    const handleSavePayment = async () => {
        if (!paymentModal.driver) {
            message.error('Chauffeur non trouvé');
            return;
        }

        setSavingPayment(true);
        try {
            const response = await affiliatesService.pay130hCommission({
                driverId: paymentModal.driver._id,
                beneficiaryId: beneficiaryId,
                beneficiaryType: type,
                month: selectedMonth,
                paymentReference: paymentRef,
                paymentStatus: paymentStatus
            });

            if (response.success) {
                message.success('Paiement enregistré avec succès');
                handleClosePaymentModal();
                fetchDetail();
            } else {
                message.error(response.message || 'Erreur lors du paiement');
            }
        } catch (error) {
            console.error('Erreur paiement 130h:', error);
            message.error('Erreur lors de l\'enregistrement du paiement');
        } finally {
            setSavingPayment(false);
        }
    };

    const getStatusTag = (driver) => {
        if (driver.commissionStatus === 'paid') {
            return <Tag color="cyan" icon={<CheckCircleOutlined />} style={{ borderRadius: 10 }}>Payé</Tag>;
        }
        if (driver.reached130h) {
            return <Tag color="orange" icon={<ClockCircleOutlined />} style={{ borderRadius: 10 }}>En attente de paiement</Tag>;
        }
        return <Tag color="default" style={{ borderRadius: 10 }}>En cours</Tag>;
    };

    // Fonction pour préparer les données d'export
    const prepareExportData = () => {
        let allDrivers = [];
        if (data?.drivers) {
            allDrivers = data.drivers;
        } else if (data?.directDrivers || data?.affiliatesWithDrivers) {
            allDrivers = [...(data.directDrivers || [])];
            (data.affiliatesWithDrivers || []).forEach(a => {
                allDrivers.push(...(a.drivers || []));
            });
        }

        return allDrivers.map(d => {
            const statusText = d.commissionStatus === 'paid' ? 'Payé' :
                d.commissionStatus === 'approved' ? 'Approuvé' :
                d.commissionStatus === 'pending' ? 'En attente' :
                d.reached130h ? 'À créer' : 'En cours';

            return {
                'Nom': `${d.firstName || ''} ${d.lastName || ''}`.trim(),
                'Téléphone': d.phone || '',
                'Heures ce mois': d.hoursThisMonth || 0,
                'Progression (%)': d.progressPercent || 0,
                [`${hoursThreshold}h atteint`]: d.reached130h ? 'Oui' : 'Non',
                'Commission (F)': d.potentialAmount || 0,
                'Mois restants': d.monthsRemaining ?? 2,
                'Statut': statusText,
                'Référence paiement': d.paymentReference || ''
            };
        });
    };

    // Export CSV
    const exportToCSV = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const benefName = `${data?.beneficiary?.firstName || ''} ${data?.beneficiary?.lastName || ''}`.trim();
            const benefType = type === 'ambassador' ? 'AMBASSADEUR' : 'AFFILIÉ';
            const titleLine = `COMMISSION ${hoursThreshold}H DE ${benefName.toUpperCase()} (${benefType})`;
            const periodLine = `PÉRIODE: ${dayjs(selectedMonth).format('MMMM YYYY').toUpperCase()}`;

            const headers = Object.keys(exportData[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '',
                headers.join(';'),
                ...exportData.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const benefNameFile = benefName.replace(/\s+/g, '_');
            const filename = `commission_${hoursThreshold}h_${benefNameFile}_${selectedMonth}_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    // Export Excel (XLS)
    const exportToExcel = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const benefName = `${data?.beneficiary?.firstName || ''} ${data?.beneficiary?.lastName || ''}`.trim();
            const benefType = type === 'ambassador' ? 'AMBASSADEUR' : 'AFFILIÉ';

            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "FA8C16" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            const titleText = `COMMISSION ${hoursThreshold}H DE ${benefName.toUpperCase()} (${benefType})`;
            const periodText = `PÉRIODE: ${dayjs(selectedMonth).format('MMMM YYYY').toUpperCase()}`;

            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                []
            ];

            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            const headers = Object.keys(exportData[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...exportData.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, `Commission ${hoursThreshold}h`);

            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length), 15) + 2
            }));
            worksheet['!cols'] = colWidths;

            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }
            ];

            worksheet['!rows'] = [
                { hpt: 25 },
                { hpt: 20 },
                { hpt: 15 },
                { hpt: 22 }
            ];

            const benefNameFile = benefName.replace(/\s+/g, '_');
            const filename = `commission_${hoursThreshold}h_${benefNameFile}_${selectedMonth}_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    // Menu d'export
    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    // Colonnes du tableau chauffeurs
    const driverColumns = [
        {
            title: 'Chauffeur',
            key: 'driver',
            width: 200,
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar
                        size={28}
                        icon={<UserOutlined />}
                        style={{
                            backgroundColor: record.reached130h ? '#52c41a' : '#fa8c16',
                            flexShrink: 0
                        }}
                    />
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {record.firstName} {record.lastName}
                        </div>
                        <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                            {record.phone}
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: 'Heures',
            key: 'hours',
            width: 120,
            render: (_, record) => (
                <span style={{ color: record.reached130h ? '#52c41a' : '#1890ff', fontWeight: 600 }}>
                    {record.hoursThisMonth || 0}h
                    <span style={{ color: '#8c8c8c', fontWeight: 400 }}> / {hoursThreshold}h</span>
                </span>
            )
        },
        {
            title: 'Progression',
            key: 'progress',
            width: 150,
            render: (_, record) => (
                <div>
                    <Progress
                        percent={record.progressPercent || 0}
                        strokeColor={record.reached130h ? '#52c41a' : '#fa8c16'}
                        trailColor="#f0f0f0"
                        size="small"
                        showInfo={false}
                        style={{ width: 120 }}
                    />
                    {record.reached130h ? (
                        <div style={{ fontSize: 11, color: '#52c41a' }}>
                            <CheckCircleOutlined style={{ marginRight: 3 }} />
                            Objectif atteint
                        </div>
                    ) : (
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            {Math.max(0, hoursThreshold - (record.hoursThisMonth || 0))}h restantes
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Mois restants',
            key: 'monthsRemaining',
            width: 100,
            render: (_, record) => {
                const remaining = record.monthsRemaining ?? 2;
                if (remaining === 0) {
                    return <Tag color="orange" style={{ borderRadius: 10 }}>Dernier mois</Tag>;
                }
                return <Tag color="blue" style={{ borderRadius: 10 }}>{remaining} mois</Tag>;
            }
        },
        {
            title: 'Commission',
            key: 'commission',
            width: 110,
            fixed: 'right',
            render: (_, record) => (
                <span style={{ fontWeight: 'bold', color: record.reached130h ? '#52c41a' : '#bfbfbf' }}>
                    {(record.potentialAmount || 0).toLocaleString()} F
                </span>
            )
        }
    ];

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!data) {
        return <Empty description="Données non trouvées" />;
    }

    const { beneficiary, summary } = data;
    const isAmbassador = beneficiary.type === 'ambassador';
    const themeColor = isAmbassador ? '#722ed1' : '#1890ff';

    // Calculer les totaux
    let allDrivers = [];
    if (data.drivers) {
        allDrivers = data.drivers;
    } else {
        allDrivers = [...(data.directDrivers || [])];
        (data.affiliatesWithDrivers || []).forEach(a => {
            allDrivers.push(...(a.drivers || []));
        });
    }
    const driversReached = allDrivers.filter(d => d.reached130h);
    const driversPaid = allDrivers.filter(d => d.commissionStatus === 'paid');
    const totalCommission = (summary.potentialAmount || 0);
    const paidAmount = driversPaid.reduce((sum, d) => sum + (d.commissionAmount || d.potentialAmount || 0), 0);
    const pendingAmount = Math.max(0, totalCommission - paidAmount);

    // Statut global de paiement
    const getGlobalPaymentStatus = () => {
        if (driversReached.length === 0) return 'none';
        if (driversPaid.length >= driversReached.length) return 'paid';
        if (driversPaid.length > 0) return 'partial';
        return 'pending';
    };
    const globalStatus = getGlobalPaymentStatus();

    return (
        <div className="commissions-130h-detail-page">
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(isFromRevenues ? `/dashboard/revenues-130h?month=${selectedMonth}` : `/dashboard/commissions-130h?month=${selectedMonth}`)}
                    >
                        Retour à la liste
                    </Button>
                    <Space>
                        <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                            <Button icon={<DownloadOutlined />} loading={exporting}>
                                Exporter
                            </Button>
                        </Dropdown>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchDetail}
                            loading={loading}
                        >
                            Actualiser
                        </Button>
                    </Space>
                </div>

                {/* Card bénéficiaire */}
                <Card>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <Avatar
                            size={70}
                            icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                            style={{
                                backgroundColor: themeColor,
                                fontSize: 32
                            }}
                        />
                        <div style={{ flex: 1 }}>
                            <Title level={3} style={{ margin: 0 }}>
                                {beneficiary.firstName} {beneficiary.lastName}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 16 }}>{beneficiary.phone}</Text>
                            <div style={{ marginTop: 8 }}>
                                <Tag
                                    color={isAmbassador ? 'purple' : 'blue'}
                                    icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                                    style={{ fontSize: 14, padding: '4px 12px' }}
                                >
                                    {isAmbassador ? 'Ambassadeur' : 'Affilié'}
                                </Tag>
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 14, color: '#888' }}>Commission {hoursThreshold}h totale</div>
                            <div style={{ fontSize: 28, fontWeight: 'bold', color: '#3f8600' }}>
                                {totalCommission.toLocaleString()} F
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Statistiques */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title={<span><TrophyOutlined style={{ marginRight: 8 }} />Commission {hoursThreshold}h</span>}
                            value={totalCommission.toLocaleString()}
                            suffix="F"
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title={<span><CheckCircleOutlined style={{ marginRight: 8 }} />Payé</span>}
                            value={paidAmount.toLocaleString()}
                            suffix="F"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title={<span><ClockCircleOutlined style={{ marginRight: 8 }} />En attente</span>}
                            value={pendingAmount.toLocaleString()}
                            suffix="F"
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title={<span><UserOutlined style={{ marginRight: 8 }} />Chauffeurs</span>}
                            value={`${driversReached.length} / ${allDrivers.length}`}
                            suffix={`atteint ${hoursThreshold}h`}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Section mois avec header style rétentions */}
            <Card
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CalendarOutlined style={{ color: '#1890ff' }} />
                            <span style={{ fontWeight: 600 }}>
                                {dayjs(selectedMonth + '-01').format('MMMM YYYY')}
                            </span>
                            <Tag color="orange">
                                <TrophyOutlined style={{ marginRight: 4 }} />
                                {totalCommission.toLocaleString()} F
                            </Tag>
                            {globalStatus === 'paid' && <Tag color="green" icon={<CheckCircleOutlined />}>Payé</Tag>}
                            {globalStatus === 'partial' && <Tag color="blue" icon={<ClockCircleOutlined />}>Partiel</Tag>}
                            {globalStatus === 'pending' && <Tag color="orange" icon={<ClockCircleOutlined />}>En attente</Tag>}
                            {globalStatus === 'none' && <Tag color="default">Aucun objectif atteint</Tag>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontSize: 13, color: '#888' }}>
                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                Heures: <strong>{(summary.totalHours || 0)}h</strong>
                                {' '} | {allDrivers.length} chauffeur(s)
                            </span>
                            <span style={{ fontSize: 12 }}>
                                <CheckCircleOutlined style={{ marginRight: 3, color: '#52c41a' }} />
                                Payé: <strong style={{ color: '#52c41a' }}>{paidAmount.toLocaleString()} F</strong>
                            </span>
                            <span style={{ fontSize: 12 }}>
                                <ClockCircleOutlined style={{ marginRight: 3, color: pendingAmount > 0 ? '#fa8c16' : '#52c41a' }} />
                                Reste: <strong style={{ color: pendingAmount > 0 ? '#fa8c16' : '#52c41a' }}>
                                    {pendingAmount.toLocaleString()} F
                                </strong>
                            </span>
                            <Button
                                size="small"
                                icon={<SyncOutlined spin={snapshotting} />}
                                onClick={handleSnapshot}
                                loading={snapshotting}
                                style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
                            >
                                Recalculer
                            </Button>
                        </div>
                    </div>
                }
            >
                {isAmbassador ? (
                    <>
                        {/* Résumé ambassadeur */}
                        <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0', marginBottom: 16, background: '#f9f0ff', borderRadius: 8, padding: 16 }}>
                            <Row gutter={16}>
                                <Col span={8} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: '#722ed1' }}>
                                        {summary.directDriversCount || 0}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Chauffeurs directs</div>
                                </Col>
                                <Col span={8} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: '#1890ff' }}>
                                        {summary.affiliatesCount || 0}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Affiliés</div>
                                </Col>
                                <Col span={8} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>
                                        {summary.indirectDriversCount || 0}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Via affiliés</div>
                                </Col>
                            </Row>
                        </div>

                        <Tabs
                            defaultActiveKey="direct"
                            items={[
                                {
                                    key: 'direct',
                                    label: (
                                        <span>
                                            <CarOutlined style={{ marginRight: 6 }} />
                                            Chauffeurs directs
                                            <Tag style={{ marginLeft: 8, borderRadius: 10 }}>
                                                {data.directDrivers?.length || 0}
                                            </Tag>
                                        </span>
                                    ),
                                    children: (
                                        <Table
                                            columns={[
                                                ...driverColumns.slice(0, -1),
                                                {
                                                    title: 'Commission',
                                                    key: 'commission',
                                                    width: 110,
                                                    render: (_, record) => (
                                                        <span style={{ fontWeight: 'bold', color: record.reached130h ? '#52c41a' : '#bfbfbf' }}>
                                                            {(record.potentialAmount || 0).toLocaleString()} F
                                                        </span>
                                                    )
                                                },
                                                {
                                                    title: '',
                                                    key: 'actions',
                                                    width: 90,
                                                    fixed: 'right',
                                                    render: (_, record) => {
                                                        if (record.commissionStatus === 'paid') {
                                                            return <Button size="small" onClick={() => handleDetailsClick(record)}>Détails</Button>;
                                                        }
                                                        if (record.reached130h) {
                                                            return (
                                                                <Button type="primary" size="small" icon={<DollarOutlined />}
                                                                    onClick={() => handlePaymentClick(record)}
                                                                    style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                                                                    Payer
                                                                </Button>
                                                            );
                                                        }
                                                        return null;
                                                    }
                                                }
                                            ]}
                                            dataSource={data.directDrivers || []}
                                            rowKey="_id"
                                            pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small' }}
                                            size="small"
                                            scroll={{ x: 750 }}
                                            locale={{ emptyText: <Empty description="Aucun chauffeur direct" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                                        />
                                    )
                                },
                                {
                                    key: 'affiliates',
                                    label: (
                                        <span>
                                            <TeamOutlined style={{ marginRight: 6 }} />
                                            Via affiliés
                                            <Tag style={{ marginLeft: 8, borderRadius: 10 }}>
                                                {summary.indirectDriversCount || 0}
                                            </Tag>
                                        </span>
                                    ),
                                    children: data.affiliatesWithDrivers?.length > 0 ? (
                                        <Collapse bordered={false} style={{ background: 'transparent' }}>
                                            {data.affiliatesWithDrivers.map(affiliate => (
                                                <Panel
                                                    key={affiliate._id}
                                                    header={
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                                <Avatar size="small" style={{ background: '#1890ff' }} icon={<TeamOutlined />} />
                                                                <span style={{ fontWeight: 600 }}>{affiliate.firstName} {affiliate.lastName}</span>
                                                                <span style={{ fontSize: 12, color: '#8c8c8c' }}>{affiliate.phone}</span>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                                <Tag color="blue">{affiliate.driversCount} chauffeur{affiliate.driversCount > 1 ? 's' : ''}</Tag>
                                                                <span style={{ color: '#fa8c16', fontWeight: 600 }}>{affiliate.totalHours}h</span>
                                                                {affiliate.driversReached130h > 0 && (
                                                                    <Tag color="green">{affiliate.driversReached130h} x {hoursThreshold}h</Tag>
                                                                )}
                                                            </div>
                                                        </div>
                                                    }
                                                    style={{ marginBottom: 8, background: '#fafafa', borderRadius: 8 }}
                                                >
                                                    <Table
                                                        columns={[
                                                            ...driverColumns.slice(0, -1),
                                                            {
                                                                title: 'Commission',
                                                                key: 'commission',
                                                                width: 110,
                                                                render: (_, record) => (
                                                                    <span style={{ fontWeight: 'bold', color: record.reached130h ? '#52c41a' : '#bfbfbf' }}>
                                                                        {(record.potentialAmount || 0).toLocaleString()} F
                                                                    </span>
                                                                )
                                                            },
                                                            {
                                                                title: '',
                                                                key: 'actions',
                                                                width: 90,
                                                                fixed: 'right',
                                                                render: (_, record) => {
                                                                    if (record.commissionStatus === 'paid') {
                                                                        return <Button size="small" onClick={() => handleDetailsClick(record)}>Détails</Button>;
                                                                    }
                                                                    if (record.reached130h) {
                                                                        return (
                                                                            <Button type="primary" size="small" icon={<DollarOutlined />}
                                                                                onClick={() => handlePaymentClick(record)}
                                                                                style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                                                                                Payer
                                                                            </Button>
                                                                        );
                                                                    }
                                                                    return null;
                                                                }
                                                            }
                                                        ]}
                                                        dataSource={affiliate.drivers || []}
                                                        rowKey="_id"
                                                        pagination={false}
                                                        size="small"
                                                    />
                                                </Panel>
                                            ))}
                                        </Collapse>
                                    ) : (
                                        <Empty description="Aucun affilié avec des chauffeurs" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                    )
                                }
                            ]}
                        />
                    </>
                ) : (
                    /* Vue affilié - tableau simple */
                    <Table
                        columns={[
                            ...driverColumns,
                            {
                                title: '',
                                key: 'actions',
                                width: 90,
                                fixed: 'right',
                                render: (_, record) => {
                                    if (record.commissionStatus === 'paid') {
                                        return <Button size="small" onClick={() => handleDetailsClick(record)}>Détails</Button>;
                                    }
                                    if (record.reached130h) {
                                        return (
                                            <Button type="primary" size="small" icon={<DollarOutlined />}
                                                onClick={() => handlePaymentClick(record)}
                                                style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                                                Payer
                                            </Button>
                                        );
                                    }
                                    return null;
                                }
                            }
                        ]}
                        dataSource={data.drivers || []}
                        rowKey="_id"
                        pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small' }}
                        size="small"
                        scroll={{ x: 750 }}
                        locale={{ emptyText: <Empty description="Aucun chauffeur" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    />
                )}
            </Card>

            {/* Modal de paiement 130h */}
            <Modal
                title={
                    <Space>
                        <DollarOutlined style={{ color: '#52c41a' }} />
                        <span>Paiement Commission {hoursThreshold}h</span>
                    </Space>
                }
                open={paymentModal.visible}
                onCancel={handleClosePaymentModal}
                footer={null}
                width={480}
            >
                {/* Mois concerné */}
                <div style={{
                    padding: '10px 16px',
                    background: '#e6f7ff',
                    borderRadius: 8,
                    border: '1px solid #91d5ff',
                    marginBottom: 16,
                    textAlign: 'center'
                }}>
                    <CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                    <span style={{ fontWeight: 600, color: '#1890ff' }}>
                        Paiement pour {dayjs(selectedMonth).format('MMMM YYYY')}
                    </span>
                </div>

                {paymentModal.driver && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 16 }}>
                            {paymentModal.driver.firstName} {paymentModal.driver.lastName}
                        </div>
                        <div style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 8 }}>
                            <PhoneOutlined style={{ marginRight: 6 }} />
                            {paymentModal.driver.phone}
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ color: '#8c8c8c' }}>Heures ce mois: </span>
                            <span style={{ fontWeight: 600, color: '#fa8c16' }}>
                                {paymentModal.driver.hoursThisMonth}h / {hoursThreshold}h
                            </span>
                        </div>
                        <div style={{
                            padding: 12,
                            background: '#f6ffed',
                            borderRadius: 8,
                            border: '1px solid #b7eb8f'
                        }}>
                            <span style={{ color: '#8c8c8c' }}>Commission: </span>
                            <span style={{ fontWeight: 700, fontSize: 18, color: '#52c41a' }}>
                                {(paymentModal.driver.potentialAmount || commissionRates.affiliate).toLocaleString()} F
                            </span>
                        </div>
                    </div>
                )}

                <Divider style={{ margin: '12px 0' }} />

                {/* Sélecteur mode de paiement */}
                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                    <Segmented
                        value={paymentMode}
                        onChange={setPaymentMode}
                        options={[
                            { label: 'Payer via Wave', value: 'wave', icon: <SendOutlined /> },
                            { label: 'Saisie manuelle', value: 'manual', icon: <EditOutlined /> }
                        ]}
                        block
                        size="large"
                    />
                </div>

                {/* Mode Wave */}
                {paymentMode === 'wave' && paymentModal.driver && (
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div style={{ marginBottom: 16, color: '#8c8c8c', fontSize: 13 }}>
                            Le paiement sera envoyé directement sur le compte Wave du bénéficiaire
                        </div>
                        <Button
                            type="primary"
                            size="large"
                            icon={<SendOutlined />}
                            loading={sendingWave}
                            onClick={handleWavePayout}
                            style={{ background: '#1890ff', borderColor: '#1890ff', height: 48, fontSize: 16, paddingInline: 32 }}
                        >
                            Envoyer {(paymentModal.driver.potentialAmount || commissionRates.affiliate).toLocaleString()} F via Wave
                        </Button>
                    </div>
                )}

                {/* Mode Manuel */}
                {paymentMode === 'manual' && (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 6, fontWeight: 500 }}>Statut de paiement</div>
                            <Select
                                value={paymentStatus}
                                onChange={setPaymentStatus}
                                style={{ width: '100%' }}
                                size="large"
                                options={[
                                    { value: 'paid', label: 'Payé' },
                                    { value: 'pending', label: 'En attente' }
                                ]}
                            />
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 6, fontWeight: 500 }}>Référence de paiement</div>
                            <Input
                                placeholder="Entrez la référence de paiement..."
                                value={paymentRef}
                                onChange={(e) => setPaymentRef(e.target.value)}
                                size="large"
                                prefix={<WalletOutlined style={{ color: '#bfbfbf' }} />}
                            />
                        </div>

                        <div style={{ textAlign: 'right' }}>
                            <Space>
                                <Button onClick={handleClosePaymentModal}>Annuler</Button>
                                <Button
                                    type="primary"
                                    loading={savingPayment}
                                    onClick={handleSavePayment}
                                >
                                    Enregistrer
                                </Button>
                            </Space>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Modal de détails du paiement */}
            <Modal
                title={
                    <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <span>Détails du paiement {hoursThreshold}h</span>
                    </Space>
                }
                open={detailsModal.visible}
                onCancel={() => setDetailsModal({ visible: false, driver: null })}
                footer={[
                    <Button key="close" onClick={() => setDetailsModal({ visible: false, driver: null })}>
                        Fermer
                    </Button>,
                    <Button
                        key="modify"
                        type="primary"
                        onClick={() => {
                            setDetailsModal({ visible: false, driver: null });
                            handlePaymentClick(detailsModal.driver);
                        }}
                    >
                        Modifier
                    </Button>
                ]}
            >
                {detailsModal.driver && (
                    <div>
                        <div style={{ padding: 16, background: '#fafafa', borderRadius: 8, marginBottom: 16 }}>
                            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 16 }}>
                                {detailsModal.driver.firstName} {detailsModal.driver.lastName}
                            </div>
                            <div style={{ color: '#8c8c8c', fontSize: 13 }}>
                                <PhoneOutlined style={{ marginRight: 6 }} />
                                {detailsModal.driver.phone}
                            </div>
                        </div>

                        <div style={{ padding: 16, background: '#e6f7ff', borderRadius: 8, border: '1px solid #91d5ff', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CalendarOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                                <div>
                                    <div style={{ color: '#8c8c8c', fontSize: 12 }}>Mois payé</div>
                                    <div style={{ fontWeight: 700, color: '#1890ff', fontSize: 16 }}>
                                        {detailsModal.driver.paidPeriod
                                            ? dayjs(detailsModal.driver.paidPeriod + '-01').format('MMMM YYYY')
                                            : 'Non spécifié'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: 16, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <DollarOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                                <div>
                                    <div style={{ color: '#8c8c8c', fontSize: 12 }}>Montant payé</div>
                                    <div style={{ fontWeight: 700, color: '#52c41a', fontSize: 18 }}>
                                        {(detailsModal.driver.commissionAmount || detailsModal.driver.potentialAmount || commissionRates.affiliate).toLocaleString()} F
                                    </div>
                                </div>
                            </div>
                        </div>

                        {detailsModal.driver.paymentReference && (
                            <div style={{ padding: 16, background: '#fff7e6', borderRadius: 8, border: '1px solid #ffd591', marginBottom: 16 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <WalletOutlined style={{ color: '#fa8c16', fontSize: 18 }} />
                                    <div>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Référence Wave</div>
                                        <div style={{ fontWeight: 600, color: '#262626' }}>
                                            {detailsModal.driver.paymentReference}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {detailsModal.driver.paidAt && (
                            <div style={{ padding: 16, background: '#fafafa', borderRadius: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
                                    <div>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Date de paiement</div>
                                        <div style={{ fontWeight: 600, color: '#262626' }}>
                                            {dayjs(detailsModal.driver.paidAt).format('DD MMMM YYYY à HH:mm')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Commissions130hDetailPage;
